import { MutationKey, useMutation } from '@tanstack/react-query'
import { useEffect } from 'react'
import { encodeFunctionData, SendCallsParameters, TransactionExecutionError, WalletCallReceipt } from 'viem'
import { base } from 'viem/chains'
import { UseSendCallsParameters, UseSendCallsReturnType, useCapabilities, useConfig } from 'wagmi'
import { sendCallsMutationOptions } from 'wagmi/query'

import type { GetWriteConfigResult } from '../types'
import { HandledWriteError } from '@/domain/errors/HandledWriteError'
import { PreWriteCheckError } from '@/domain/errors/PreWriteCheckError'
import { useOriginChainId } from '@/domain/hooks/useOriginChainId'
import { useConnectedAddress } from '@/domain/wallet/useConnectedAddress'
import { useWaitForTransactionReceiptUniversal } from '@/features/actions/logic/transactions/useWaitForTransactionReceiptUniversal'
import { JSONStringifyRich } from '@/utils/object'
import { captureError } from '@/utils/sentry'

export type Eip5792WriteStatus =
  | { kind: 'disabled' }
  | { kind: 'ready' }
  | { kind: 'tx-sending' }
  | { kind: 'tx-confirming' }
  | { kind: 'success' }
  | { kind: 'error'; errorKind: Eip5792WriteErrorKind; error: Error }

export type Eip5792WriteErrorKind =
  | 'submission'
  | 'before-write'
  | 'confirmation'
  | 'tx-reverted'
  | 'wallet-upgrade-rejection'

export interface UseEip5792WriteResult {
  write: () => void
  reset: () => void
  status: Eip5792WriteStatus
  receipts: WalletCallReceipt<bigint, 'success' | 'reverted'>[] | undefined
}

export interface UseEip5792WriteCallbacks {
  onTransactionSettled?: {
    mounted?: (txReceipt: WalletCallReceipt<bigint, 'success' | 'reverted'>[]) => void
    unmounted?: () => void
  }
}

export interface UseEip5792WriteParams {
  contracts: GetWriteConfigResult[]
  enabled?: boolean
  callbacks?: UseEip5792WriteCallbacks
  onBeforeWrite?: () => void
  mutationKeyPrefix?: MutationKey
}

export function useEip5792Write({
  contracts,
  enabled = true,
  callbacks = {},
  onBeforeWrite,
  mutationKeyPrefix,
}: UseEip5792WriteParams): UseEip5792WriteResult {
  const chainId = useOriginChainId()

  const { account: address } = useConnectedAddress({ throwOnNotConnected: false })
  const { data: availableCapabilities } = useCapabilities({
    account: address,
    query: {
      enabled: enabled && !!address,
    },
  })

  const capabilities = (() => {
    const paymasterUrl = import.meta.env.VITE_BASE_PAYMASTER_URL

    if (!availableCapabilities || chainId !== base.id || !paymasterUrl) {
      return undefined
    }

    const chainCapabilities = availableCapabilities[chainId]
    if (chainCapabilities?.paymasterService?.supported) {
      return {
        paymasterService: {
          url: `${import.meta.env.VITE_BASE_PAYMASTER_URL}`,
        },
      }
    }
  })()

  const {
    sendCalls,
    data: sendCallsResult,
    isSuccess: wasTxSent,
    status: sendCallsStatus,
    error: sendCallsError,
    reset,
  } = useSendCalls({
    mutationKey: [...(mutationKeyPrefix ?? []), ...getSendCallsMutationKey({ chainId, contracts })],
    onBeforeWrite,
  })

  const {
    data: callsStatusData,
    status: callsStatusQueryStatus,
    error: callsStatusError,
    isLoading: isCallsStatusLoading,
  } = useWaitForTransactionReceiptUniversal(
    {
      type: 'eip-5792',
      callsId: sendCallsResult?.id,
    },
    {
      onSettledUnmounted: callbacks.onTransactionSettled?.unmounted,
    },
  )

  const receipts = callsStatusData?.status === 'success' ? callsStatusData.receipts : undefined
  // biome-ignore lint(useExhaustiveDependencies): callback should only be called when receipts changes
  useEffect(() => {
    if (receipts) {
      callbacks.onTransactionSettled?.mounted?.(receipts)
    }
  }, [receipts])

  const status = ((): Eip5792WriteStatus => {
    if (wasTxSent && callsStatusQueryStatus === 'success' && callsStatusData?.status === 'success') {
      return { kind: 'success' }
    }

    if (sendCallsStatus === 'pending') {
      return { kind: 'tx-sending' }
    }

    if (wasTxSent && (isCallsStatusLoading || callsStatusData?.status === 'pending')) {
      return { kind: 'tx-confirming' }
    }

    if (!enabled) {
      return { kind: 'disabled' }
    }

    if (
      sendCallsError instanceof TransactionExecutionError &&
      (sendCallsError.cause.name === 'AtomicReadyWalletRejectedUpgradeError' ||
        (sendCallsError.cause as any).code === 5750)
    ) {
      return { kind: 'error', errorKind: 'wallet-upgrade-rejection', error: sendCallsError }
    }

    if (sendCallsError) {
      if (sendCallsError instanceof PreWriteCheckError) {
        return { kind: 'error', errorKind: 'before-write', error: sendCallsError }
      }
      return { kind: 'error', errorKind: 'submission', error: sendCallsError }
    }

    if (wasTxSent && callsStatusError) {
      return { kind: 'error', errorKind: 'confirmation', error: callsStatusError }
    }

    if (wasTxSent && callsStatusQueryStatus === 'success' && callsStatusData?.status === 'reverted') {
      return { kind: 'error', errorKind: 'tx-reverted', error: callsStatusData.error }
    }

    return { kind: 'ready' }
  })()

  const error = status.kind === 'error' ? status.error : undefined
  useEffect(() => {
    if (error) {
      captureError(new HandledWriteError(error))
    }
  }, [error])

  const finalWrite = enabled
    ? () => {
        const calls = contracts.map((contract) => {
          const { address, abi, functionName, args, value } = contract
          return {
            data: encodeFunctionData({
              abi,
              functionName,
              args,
            }),
            to: address,
            value,
          } satisfies SendCallsParameters['calls'][number]
        })

        sendCalls({ calls, capabilities })
      }
    : () => {}

  return {
    reset,
    write: finalWrite,
    status,
    receipts,
  }
}

function getSendCallsMutationKey({
  chainId,
  contracts,
}: {
  chainId: number
  contracts: GetWriteConfigResult[]
}): MutationKey {
  const calls = contracts.map((contract) => ({
    address: contract.address,
    functionName: contract.functionName,
    args: contract.args,
    value: contract.value,
  }))

  return ['sendCalls', chainId, JSONStringifyRich(calls)]
}

// @note: This function is a copy of Wagmi's useSendCalls with an option to pass a mutationKey.
function useSendCalls(
  parameters: UseSendCallsParameters & { mutationKey?: MutationKey; onBeforeWrite?: () => void } = {},
): UseSendCallsReturnType {
  const { mutation, mutationKey, onBeforeWrite } = parameters

  const config = useConfig(parameters)

  const mutationOptions = sendCallsMutationOptions(config)
  const { mutate, mutateAsync, ...result } = useMutation({
    ...mutation,
    ...mutationOptions,
    mutationKey,
    onMutate: async () => {
      try {
        onBeforeWrite?.()
      } catch (error: unknown) {
        throw new PreWriteCheckError(error)
      }
    },
  })

  type Return = UseSendCallsReturnType
  return {
    ...result,
    sendCalls: mutate as Return['sendCalls'],
    sendCallsAsync: mutateAsync as Return['sendCallsAsync'],
  }
}
